import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, User as UserIcon, Save, Sparkles, Lock, Mail, Brain } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppNav from '@/components/AppNav';
import SEO from '@/components/SEO';
import AppShell from '@/components/AppShell';

const API = process.env.REACT_APP_BACKEND_URL;

const AGE_RANGES = [
  { value: '18-27', label: '18 – 27' },
  { value: '28-37', label: '28 – 37' },
  { value: '38-47', label: '38 – 47' },
  { value: '48-57', label: '48 – 57' },
  { value: '58-67', label: '58 – 67' },
  { value: '68+', label: '68+' },
];

const GENDERS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const ProfilePage = () => {
  const { user, isAuthenticated, getAuthHeader, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === '1';
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    gender: '',
    age_range: '',
    has_kids: null,
    has_pets: null,
    region: '',
    ai_notes: '',
  });
  const [formReady, setFormReady] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setForm({
        gender: user.gender || '',
        age_range: user.age_range || '',
        has_kids: typeof user.has_kids === 'boolean' ? user.has_kids : null,
        has_pets: typeof user.has_pets === 'boolean' ? user.has_pets : null,
        region: user.region || '',
        ai_notes: user.ai_notes || '',
      });
      setFormReady(true);
    }
  }, [user]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only include fields the user has explicitly set. The backend leaves
      // omitted fields untouched, preventing accidental clears caused by
      // partially-hydrated form state.
      const payload = {};
      if (form.gender) payload.gender = form.gender;
      if (form.age_range) payload.age_range = form.age_range;
      if (typeof form.has_kids === 'boolean') payload.has_kids = form.has_kids;
      if (typeof form.has_pets === 'boolean') payload.has_pets = form.has_pets;
      if (form.region) payload.region = form.region;
      // ai_notes: send explicit clear when the field has been emptied, so the
      // backend nulls it out on the user document.
      payload.ai_notes = form.ai_notes && form.ai_notes.trim() ? form.ai_notes.trim() : '__clear__';

      await axios.put(`${API}/api/auth/profile`, payload, { headers: getAuthHeader() });
      await refreshUser();
      if (isWelcome) {
        toast.success('Settings saved! Let\'s create your first idea.');
        navigate('/dashboard');
      } else {
        toast.success('Settings saved! Poettik will use these to tailor your scripts.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Could not save profile. Please try again.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !user || !formReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SEO title="Settings" description="Update your account details and creator profile on Poettik." noindex />
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AppShell title="Settings">
      <main data-testid="profile-page" className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-32">
        {isWelcome && (
          <div
            className="mb-8 rounded-2xl bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/10 border border-primary/30 p-5 sm:p-6"
            data-testid="welcome-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-lg sm:text-xl font-bold mb-1">
                  Welcome to Poettik! Take 30 seconds to set this up
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  These details stay private but completely change how your scripts sound. Poettik tailors hooks, pain points and humour to creators like you — a 28-year-old dog-owning Pro creator gets very different angles to a 45-year-old parent.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Why each field matters:</strong> age &amp; gender shape the tone and references · kids unlocks parent-friendly angles · pets unlocks animal-loving hooks · region picks British vs American spelling and slang · AI notes let you nudge the model with your own style rules.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="mt-3 text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  data-testid="welcome-skip"
                >
                  Skip for now →
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
            <UserIcon className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">
            Your <span className="gradient-text">Settings</span>
          </h1>
          <p className="text-muted-foreground">
            Tune the way Poettik sounds for you — creator profile, personal AI notes, and account details.
          </p>
        </div>

        <Card variant="glass">
          <CardHeader>
            <CardTitle className="text-lg">Personalisation</CardTitle>
            <CardDescription>
              Optional — leave any field blank and Poettik will skip it. You can update this anytime.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setField('gender', v)}>
                  <SelectTrigger data-testid="profile-gender-trigger">
                    <SelectValue placeholder="Choose..." />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} data-testid={`profile-gender-${opt.value}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Age range</Label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {AGE_RANGES.map((opt) => {
                    const selected = form.age_range === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setField('age_range', selected ? '' : opt.value)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-primary/15 border-primary/40 text-primary'
                            : 'border-border text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`profile-age-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Do you have kids?</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: true, label: 'Yes' },
                    { v: false, label: 'No' },
                  ].map((opt) => {
                    const selected = form.has_kids === opt.v;
                    return (
                      <button
                        key={String(opt.v)}
                        type="button"
                        onClick={() => setField('has_kids', selected ? null : opt.v)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-primary/15 border-primary/40 text-primary'
                            : 'border-border text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`profile-kids-${opt.label.toLowerCase()}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Do you have pets?</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: true, label: 'Yes' },
                    { v: false, label: 'No' },
                  ].map((opt) => {
                    const selected = form.has_pets === opt.v;
                    return (
                      <button
                        key={String(opt.v)}
                        type="button"
                        onClick={() => setField('has_pets', selected ? null : opt.v)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-primary/15 border-primary/40 text-primary'
                            : 'border-border text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`profile-pets-${opt.label.toLowerCase()}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Region</Label>
                <p className="text-xs text-muted-foreground">
                  Sets your script tone (British vs American), the currency shown on pricing, and date formats.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: 'uk', label: '🇬🇧 UK', desc: 'British tone · £ GBP' },
                    { v: 'us', label: '🇺🇸 US', desc: 'American tone · $ USD' },
                  ].map((opt) => {
                    const selected = form.region === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setField('region', opt.v)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors text-left ${
                          selected
                            ? 'bg-primary/15 border-primary/40 text-primary'
                            : 'border-border text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`profile-region-${opt.v}`}
                      >
                        <div>{opt.label}</div>
                        <div className="text-[10px] opacity-70 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Poettik uses these details only to tailor the tone, references and pain points in your scripts.
                  We never share your profile.
                </p>
              </div>

              <div className="space-y-2 pt-2 border-t border-border">
                <Label htmlFor="ai-notes" className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-accent" />
                  Personal AI notes
                  <span className="text-[10px] font-normal text-muted-foreground">(optional)</span>
                </Label>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Nudge the AI with your own style rules. These sit on top of the admin&rsquo;s master instructions
                  (which always win) and get injected into every generation you run — hooks, scripts, filming
                  ideas and skits. Great for things like: <em>&ldquo;always end with a punchy question&rdquo;</em>,
                  <em> &ldquo;avoid emojis&rdquo;</em>, <em>&ldquo;keep hooks under 8 words&rdquo;</em>, or your niche&rsquo;s slang.
                </p>
                <Textarea
                  id="ai-notes"
                  value={form.ai_notes}
                  onChange={(e) => setField('ai_notes', e.target.value.slice(0, 2000))}
                  rows={5}
                  maxLength={2000}
                  placeholder="e.g. Always open with a bold claim, never use emojis, keep the script under 25 words per beat, lean into dry British humour…"
                  data-testid="profile-ai-notes"
                />
                <p className="text-[11px] text-muted-foreground text-right">
                  {form.ai_notes.length} / 2000
                </p>
              </div>

              <Button
                type="submit"
                variant="premium"
                size="lg"
                className="w-full"
                disabled={saving}
                data-testid="profile-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <VoiceoverKeysSection />

        <AccountSection />
      </main>
    </AppShell>
  );
};


const VoiceoverKeysSection = () => {
  const { getAuthHeader } = useAuth();
  const [status, setStatus] = useState(null);
  const [eleven, setEleven] = useState('');
  const [fish, setFish] = useState('');
  const [saving, setSaving] = useState('');
  const [affiliates, setAffiliates] = useState({ elevenlabs: 'https://elevenlabs.io', fish: 'https://fish.audio' });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/voiceover/keys`, { headers: getAuthHeader() });
      setStatus(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  // Affiliate sign-up links (same source as the product voiceover section).
  useEffect(() => {
    axios.get(`${API}/api/voiceover/providers`)
      .then((res) => {
        const map = {};
        (res.data.providers || []).forEach((p) => { if (p.affiliate_url) map[p.key] = p.affiliate_url; });
        setAffiliates((prev) => ({ ...prev, ...map }));
      })
      .catch(() => { /* keep fallbacks */ });
  }, []);

  const saveKey = async (provider, value) => {
    if (!value.trim()) return;
    setSaving(provider);
    try {
      await axios.put(`${API}/api/voiceover/keys`, { provider, api_key: value.trim() }, { headers: getAuthHeader() });
      toast.success('API key saved.');
      setEleven(''); setFish('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that key.');
    } finally { setSaving(''); }
  };

  const removeKey = async (provider) => {
    try {
      await axios.delete(`${API}/api/voiceover/keys/${provider}`, { headers: getAuthHeader() });
      toast.success('Key removed.');
      await load();
    } catch { toast.error('Could not remove key.'); }
  };

  const row = (provider, label, value, setValue, signupUrl) => {
    const info = status?.[provider];
    return (
      <div className="space-y-2" data-testid={`vo-key-${provider}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{label}</span>
          {info?.set ? (
            <span className="text-xs text-emerald-400">Connected ····{info.last4}</span>
          ) : (
            <a href={signupUrl} target="_blank" rel="sponsored noopener noreferrer" className="text-xs text-primary hover:underline">Get a key →</a>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            type="password"
            placeholder={info?.set ? 'Replace key…' : `Paste your ${label} API key`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={`vo-key-input-${provider}`}
          />
          <Button variant="outline" size="sm" onClick={() => saveKey(provider, value)} disabled={saving === provider} data-testid={`vo-key-save-${provider}`}>
            {saving === provider ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
          {info?.set && (
            <Button variant="ghost" size="sm" onClick={() => removeKey(provider)} data-testid={`vo-key-remove-${provider}`}>Remove</Button>
          )}
        </div>
      </div>
    );
  };

  const freeUsed = status?.free_used ?? 0;
  const freeLimit = status?.free_limit ?? 2;

  return (
    <Card variant="glass" className="mt-8" data-testid="voiceover-keys-card">
      <CardHeader>
        <CardTitle className="text-lg">Voiceover API keys</CardTitle>
        <CardDescription>
          You get {freeLimit} free voiceovers on us ({Math.max(0, freeLimit - freeUsed)} left). After that, add your own
          ElevenLabs or Fish Audio key here to keep generating — your key, your usage, no limits from us.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {row('elevenlabs', 'ElevenLabs', eleven, setEleven, affiliates.elevenlabs || 'https://elevenlabs.io')}
        {row('fish', 'Fish Audio', fish, setFish, affiliates.fish || 'https://fish.audio')}
      </CardContent>
    </Card>
  );
};

const AccountSection = () => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || '');
    setEmail(user?.email || '');
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error('Enter your current password to confirm changes');
      return;
    }

    const payload = { current_password: currentPassword };
    if (name && name.trim() !== (user?.name || '').trim()) payload.new_name = name.trim();
    if (email && email.toLowerCase() !== (user?.email || '').toLowerCase()) payload.new_email = email.trim().toLowerCase();
    if (newPassword) {
      if (newPassword.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('New password and confirmation do not match');
        return;
      }
      payload.new_password = newPassword;
    }

    if (Object.keys(payload).length === 1) {
      toast.error('Nothing to change');
      return;
    }

    setSaving(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.put(`${API}/api/auth/account`, payload, { headers: getAuthHeader() });
      await refreshUser();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Account updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="glass" className="mt-6" data-testid="account-section">
      <CardHeader>
        <CardTitle className="text-lg">Account</CardTitle>
        <CardDescription>
          Change your name, email, or password. We'll ask for your current password to confirm.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-10" value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="email" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>New password (optional)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  placeholder="Leave blank to keep current"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="account-new-password"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm new password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  className="pl-10"
                  placeholder="Repeat new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="account-confirm-password"
                />
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border/40 space-y-2">
            <Label className="text-amber-400">Confirm with current password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="password"
                className="pl-10"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="account-current-password"
              />
            </div>
          </div>

          <Button type="submit" variant="premium" size="lg" className="w-full" disabled={saving} data-testid="account-save-btn">
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Account Changes
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProfilePage;
